import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calculator, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  INDEX_CODES,
  computeIndexation,
  indexationInputForLease,
  renderAnnexHtml,
  type IndexationResult,
} from '../../../supabase/functions/_shared/indexation.ts';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const eur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

// Reads the « Indexation annuelle » fields of the contract form.
export function indexationFieldsFromForm(formData: FormData) {
  const num = (k: string) => {
    const v = formData.get(k) as string | null;
    return v === null || v === '' ? null : parseFloat(v);
  };
  return {
    indexation_enabled: formData.get('ix_enabled') === '1',
    indexation_index: (formData.get('ix_index') as string) || 'ICC',
    indexation_quarter: num('ix_quarter') ?? 2,
    indexation_invoice_month: num('ix_invoice_month') ?? 10,
    indexation_effective_month: num('ix_effective_month') ?? 11,
    indexation_base_year: num('ix_base_year'),
    indexation_base_value: num('ix_base_value'),
    monthly_rent: num('ix_monthly_rent'),
    deposit_months: num('ix_deposit_months') ?? 3,
  };
}

export function IndexationSettings({ lease }: { lease: any }) {
  const [enabled, setEnabled] = useState<boolean>(!!lease?.indexation_enabled);
  const [index, setIndex] = useState<string>(lease?.indexation_index || 'ICC');
  const [quarter, setQuarter] = useState(String(lease?.indexation_quarter ?? 2));
  const [invoiceMonth, setInvoiceMonth] = useState(String(lease?.indexation_invoice_month ?? 10));
  const [effectiveMonth, setEffectiveMonth] = useState(String(lease?.indexation_effective_month ?? 11));
  const [baseYear, setBaseYear] = useState(lease?.indexation_base_year != null ? String(lease.indexation_base_year) : '');
  const [baseValue, setBaseValue] = useState(lease?.indexation_base_value != null ? String(lease.indexation_base_value) : '');
  const [monthlyRent, setMonthlyRent] = useState(lease?.monthly_rent != null ? String(lease.monthly_rent) : '');
  const [depositMonths, setDepositMonths] = useState(String(lease?.deposit_months ?? 3));

  const [simulating, setSimulating] = useState(false);
  const [missingIndex, setMissingIndex] = useState<{ year: number } | null>(null);
  const [newIndexValue, setNewIndexValue] = useState('');
  const [preview, setPreview] = useState<{ year: number; result: IndexationResult; annex: string } | null>(null);

  const simYear = (parseInt(baseYear) || new Date().getFullYear() - 1) + 1;
  const draftLease = () => ({
    ...lease,
    indexation_index: index,
    indexation_quarter: parseInt(quarter),
    indexation_invoice_month: parseInt(invoiceMonth),
    indexation_effective_month: parseInt(effectiveMonth),
    indexation_base_year: parseInt(baseYear),
    indexation_base_value: parseFloat(baseValue),
    monthly_rent: monthlyRent === '' ? null : parseFloat(monthlyRent),
    deposit_months: parseFloat(depositMonths),
  });

  const simulate = async () => {
    if (!(parseFloat(baseValue) > 0) || !baseYear) {
      toast.error("Renseignez l'année et la valeur de l'indice de base");
      return;
    }
    setSimulating(true);
    setPreview(null);
    setMissingIndex(null);
    try {
      const { data: iv, error } = await (supabase as any).from('index_values').select('value')
        .eq('index_code', index).eq('year', simYear).eq('quarter', parseInt(quarter)).maybeSingle();
      if (error) throw error;
      if (!iv) {
        setMissingIndex({ year: simYear });
        return;
      }
      const period = `${simYear}-${invoiceMonth.padStart(2, '0')}`;
      const input = indexationInputForLease(draftLease(), period, Number(iv.value));
      const result = computeIndexation(input);
      const t = lease?.tenants;
      const annex = renderAnnexHtml({
        sciName: lease?.properties?.scis?.name ?? '',
        tenantName: t?.company_name || `${t?.first_name ?? ''} ${t?.last_name ?? ''}`.trim(),
        propertyLabel: [lease?.properties?.name, lease?.properties?.address].filter(Boolean).join(' — '),
        input, result,
      });
      setPreview({ year: simYear, result, annex });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSimulating(false);
    }
  };

  const saveIndex = async () => {
    const value = parseFloat(newIndexValue);
    if (!(value > 0) || !missingIndex) return;
    const { error } = await (supabase as any).from('index_values')
      .upsert({ index_code: index, year: missingIndex.year, quarter: parseInt(quarter), value });
    if (error) {
      toast.error(`Indice non enregistré : ${error.message}`);
      return;
    }
    toast.success(`${index} T${quarter} ${missingIndex.year} enregistré`);
    setNewIndexValue('');
    await simulate();
  };

  const openAnnex = () => {
    if (!preview) return;
    const url = URL.createObjectURL(new Blob([preview.annex], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const monthSelect = (value: string, onChange: (v: string) => void) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
      <input type="hidden" name="ix_enabled" value={enabled ? '1' : '0'} />
      <input type="hidden" name="ix_index" value={index} />
      <input type="hidden" name="ix_quarter" value={quarter} />
      <input type="hidden" name="ix_invoice_month" value={invoiceMonth} />
      <input type="hidden" name="ix_effective_month" value={effectiveMonth} />
      <input type="hidden" name="ix_base_year" value={baseYear} />
      <input type="hidden" name="ix_base_value" value={baseValue} />
      <input type="hidden" name="ix_monthly_rent" value={monthlyRent} />
      <input type="hidden" name="ix_deposit_months" value={depositMonths} />

      <div className="flex items-center justify-between">
        <Label htmlFor="ix-enabled">Indexation annuelle</Label>
        <Switch id="ix-enabled" checked={enabled} onCheckedChange={setEnabled} />
      </div>

      {enabled && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Indice</Label>
              <Select value={index} onValueChange={setIndex}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDEX_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Trimestre de référence</Label>
              <Select value={quarter} onValueChange={setQuarter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>T{q}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Quittance portant la révision</Label>
              {monthSelect(invoiceMonth, setInvoiceMonth)}
            </div>
            <div className="space-y-1">
              <Label>Nouveau loyer à compter de</Label>
              {monthSelect(effectiveMonth, setEffectiveMonth)}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ix-base-year">Année de l'indice de base</Label>
              <Input id="ix-base-year" type="number" value={baseYear} onChange={(e) => setBaseYear(e.target.value)} placeholder="2025" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ix-base-value">Indice de base</Label>
              <Input id="ix-base-value" type="number" step="0.01" value={baseValue} onChange={(e) => setBaseValue(e.target.value)} placeholder="2086" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ix-monthly-rent">Loyer mensuel HT indexé (€)</Label>
              <Input id="ix-monthly-rent" type="number" step="0.01" value={monthlyRent} onChange={(e) => setMonthlyRent(e.target.value)} placeholder="Loyer / nb de mois" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ix-deposit-months">Dépôt de garantie (mois de loyer)</Label>
              <Input id="ix-deposit-months" type="number" step="0.5" value={depositMonths} onChange={(e) => setDepositMonths(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Ne modifiez pas le loyer à la main : la révision est appliquée automatiquement à la création de la quittance
            de {MONTHS[parseInt(invoiceMonth) - 1].toLowerCase()} (lignes détaillées, ajustement du dépôt et annexe), puis le contrat est mis à jour.
          </p>

          <Button type="button" variant="outline" size="sm" onClick={simulate} disabled={simulating}>
            {simulating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
            Simuler la révision {simYear}
          </Button>

          {missingIndex && (
            <div className="space-y-2 text-sm">
              <p className="text-destructive">
                {index} T{quarter} {missingIndex.year} non saisi : la quittance de {MONTHS[parseInt(invoiceMonth) - 1].toLowerCase()} sera bloquée.
              </p>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={newIndexValue} onChange={(e) => setNewIndexValue(e.target.value)} placeholder={`${index} T${quarter} ${missingIndex.year}`} className="w-40" />
                <Button type="button" size="sm" onClick={saveIndex}>Enregistrer l'indice</Button>
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-2 text-sm">
              <p>
                Nouveau loyer mensuel : <strong>{eur(preview.result.newMonthlyRent)} HT</strong>
                {' '}({preview.result.variationPct >= 0 ? '+' : ''}{preview.result.variationPct} %) — dépôt : {eur(preview.result.newDeposit)}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ligne</TableHead>
                    <TableHead className="text-right">HT</TableHead>
                    <TableHead className="text-right">TVA</TableHead>
                    <TableHead className="text-right">TTC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.result.lines.map((l) => (
                    <TableRow key={l.label}>
                      <TableCell>
                        {l.label}
                        {l.annotation && <div className="text-xs text-muted-foreground italic">{l.annotation}</div>}
                      </TableCell>
                      <TableCell className="text-right">{eur(l.amountHT)}</TableCell>
                      <TableCell className="text-right">{l.vatRate ? `${l.vatRate} %` : '-'}</TableCell>
                      <TableCell className="text-right">{eur(l.amountTTC)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-semibold">Total à régler</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right font-semibold">{eur(preview.result.totals.toPay)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Button type="button" variant="link" size="sm" className="px-0" onClick={openAnnex}>Voir l'annexe</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
